import Image from 'next/image';
import NextLink from 'next/link';
import { oneLine } from 'common-tags';

import { Button, Container, RichInline } from '@/atoms';
import { ButtonSize, ButtonVariant } from '@/atoms/button/types';

import type { FunctionComponent } from 'react';
import type { TypeContactCtaProps } from './types';

/**
 * Hero CTA full-bleed "¿Aún sigues perdido?" (Figma HoM). Renderizado
 * por el `Prefooter`.
 */
export const ContactCta: FunctionComponent<TypeContactCtaProps> = ({
  className = '',
  title,
  subtitle,
  backgroundImage,
  button,
  titleStyleClass,
  subtitleStyleClass,
}: TypeContactCtaProps) => (
  <section
    className={`bg-primary-900 relative isolate w-full overflow-hidden ${className}`}
  >
    {backgroundImage?.src && (
      <Image
        src={backgroundImage.src}
        alt={backgroundImage.alt ?? ''}
        fill
        sizes="100vw"
        className="-z-20 object-cover"
      />
    )}
    <div
      aria-hidden
      className="absolute inset-0 -z-10 bg-gradient-to-r from-black/60 via-black/30 to-transparent"
    />

    <Container wrapper>
      <div className="flex flex-col items-start gap-8 py-16 md:py-24 lg:max-w-[760px]">
        <div className="flex flex-col gap-2">
          {title && (
            <RichInline
              as="h2"
              html={title}
              className={oneLine`text-headline text-neutrals-0 font-bold ${
                titleStyleClass ??
                'text-[40px] leading-[48px] md:text-[64px] md:leading-[72px]'
              }`}
            />
          )}
          {subtitle && (
            <RichInline
              as="p"
              html={subtitle}
              className={oneLine`text-body text-text-neutrals-0 ${
                subtitleStyleClass ??
                'text-[20px] leading-[28px] md:text-[24px] md:leading-[32px]'
              }`}
            />
          )}
        </div>

        {button?.label && button?.href && (
          <NextLink href={button.href} prefetch={false} passHref legacyBehavior>
            <Button
              as="a"
              size={ButtonSize.MEDIUM}
              variant={ButtonVariant.SECONDARY}
            >
              {button.label}
            </Button>
          </NextLink>
        )}
      </div>
    </Container>
  </section>
);

ContactCta.displayName = 'ContactCta';
